import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import {
  X,
  Download,
  Package,
  Globe,
  Truck,
  Eye,
  Edit2,
  AlertCircle,
  CheckCircle,
  Loader2,
  FileDown,
  Box,
  Weight
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Separator } from './ui/separator';
import { Badge } from './ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { Order, PackageDimensions, Product } from '../types';
import { formatCurrency, formatDate } from '../lib/utils';
import { getPackageDimensions } from '../services/packageDimensionsService';
import { fetchProductById, searchProducts } from '../services/productService';
import { fetchBundleById } from '../services/bundleService';
import {
  generateDhlCsvData,
  convertToCsvString,
  downloadCsvFile,
  generateDhlCsvFilename,
  inchesToCm,
  ouncesToKg,
  DhlCsvRowData,
  EnhancedProductData
} from '../lib/dhlCsvUtils';

interface DhlExportModalProps {
  order: Order | null;
  isOpen: boolean;
  onClose: () => void;
}

type Step = 'preview' | 'edit' | 'package' | 'generate';

export function DhlExportModal({ order, isOpen, onClose }: DhlExportModalProps) {
  const [currentStep, setCurrentStep] = useState<Step>('preview');
  const [packageDimensions, setPackageDimensions] = useState<PackageDimensions[]>([]);
  const [selectedPackageId, setSelectedPackageId] = useState<string>('');
  const [selectedPackage, setSelectedPackage] = useState<PackageDimensions | null>(null);
  const [isLoadingPackages, setIsLoadingPackages] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [csvData, setCsvData] = useState<DhlCsvRowData[]>([]);
  const [editableData, setEditableData] = useState<Partial<DhlCsvRowData>>({});
  const [error, setError] = useState<string | null>(null);
  const [productData, setProductData] = useState<EnhancedProductData>({});
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [expandedOrder, setExpandedOrder] = useState<Order | null>(null);

  // Load package dimensions and product data when modal opens
  useEffect(() => {
    if (isOpen && order) {
      loadPackageDimensions();
      loadProductData();
    } else {
      // Reset state when modal closes
      setCurrentStep('preview');
      setSelectedPackageId('');
      setSelectedPackage(null);
      setCsvData([]);
      setEditableData({});
      setError(null);
      setProductData({});
      setExpandedOrder(null);
    }
  }, [isOpen, order]);

  // Update selected package when ID changes
  useEffect(() => {
    if (selectedPackageId && packageDimensions.length > 0) {
      const pkg = packageDimensions.find(p => p.id === selectedPackageId);
      setSelectedPackage(pkg || null);
    } else {
      setSelectedPackage(null);
    }
  }, [selectedPackageId, packageDimensions]);

  // Generate CSV data when package is selected and product data is loaded
  useEffect(() => {
    if (expandedOrder && selectedPackage) {
      const data = generateDhlCsvData(expandedOrder, selectedPackage, productData, editableData);
      setCsvData(data);
    } else {
      setCsvData([]);
    }
  }, [expandedOrder, selectedPackage, productData, editableData]);

  const loadPackageDimensions = async () => {
    try {
      setIsLoadingPackages(true);
      const dimensions = await getPackageDimensions();
      setPackageDimensions(dimensions);
    } catch (error) {
      console.error('Error loading package dimensions:', error);
      setError('Failed to load package dimensions. Please check your settings.');
    } finally {
      setIsLoadingPackages(false);
    }
  };

  const loadProductData = async () => {
    if (!order) return;
    
    try {
      setIsLoadingProducts(true);
      const enhancedData: EnhancedProductData = {};
      const expandedItems: Order['items'] = [];
      const productCache = new Map<string, Product | null>();
      const bundleCache = new Map<string, Awaited<ReturnType<typeof fetchBundleById>> | null>();

      const getProduct = async (productId: string, fallbackName?: string) => {
        if (!productId) return null;
        if (!productCache.has(productId)) {
          let product: Product | null = null;
          try {
            product = await fetchProductById(productId);
          } catch (error) {
            console.warn(`Failed to fetch product ${productId}:`, error);
          }

          if (!product) {
            try {
              const searchTerm = fallbackName?.trim() || productId;
              const searchResults = await searchProducts(searchTerm);
              const normalizedName = fallbackName?.trim().toLowerCase();
              product = searchResults.products.find(p => {
                if (p.id === productId) return true;
                if (normalizedName) {
                  return p.name.trim().toLowerCase() === normalizedName;
                }
                return false;
              }) || null;
            } catch (error) {
              console.warn(`Failed to search for product ${productId}:`, error);
            }
          }

          productCache.set(productId, product);
        }
        return productCache.get(productId) ?? null;
      };

      for (const item of order.items) {
        if (item.id?.startsWith('bundle:')) {
          const [, bundleIdRaw] = item.id.split(':');
          const bundleId = bundleIdRaw?.trim();

          if (!bundleId) {
            expandedItems.push({ ...item });
            continue;
          }

          if (!bundleCache.has(bundleId)) {
            try {
              const bundle = await fetchBundleById(bundleId);
              bundleCache.set(bundleId, bundle);
            } catch (error) {
              console.error(`Failed to fetch bundle ${bundleId}:`, error);
              bundleCache.set(bundleId, null);
            }
          }

          const bundle = bundleCache.get(bundleId);

          if (!bundle || !Array.isArray(bundle.items) || bundle.items.length === 0) {
            expandedItems.push({ ...item });
            continue;
          }

          const bundleOrderQuantity = item.quantity || 1;

          for (const bundleItem of bundle.items) {
            const productId = bundleItem.productId;
            const perBundleQty = bundleItem.quantity || 1;
            const listingQty = bundleItem.listingQuantity || 1;
            const totalOrderQty = perBundleQty * bundleOrderQuantity;
            const bundleItemNameHint = bundleItem.productName || productId;

            const product = await getProduct(productId, bundleItemNameHint);
            const resolvedName = bundleItem.productName || product?.name || productId;

            if (product) {
              enhancedData[productId] = product;
            }

            const unitPrice = product?.price ?? 0;
            const image = product?.image || bundleItem.image || '';
            const listingQuantity = bundleItem.listingQuantity || product?.listingQuantity || 1;

            expandedItems.push({
              id: productId,
              name: resolvedName,
              quantity: totalOrderQty,
              price: unitPrice,
              onSale: false,
              image,
              total: unitPrice * totalOrderQty,
              listingQuantity,
            });
          }
        } else {
          const product = await getProduct(item.id, item.name);
          if (product) {
            enhancedData[item.id] = product;
          }

          expandedItems.push({
            ...item,
            image: item.image || product?.image || '',
            listingQuantity: item.listingQuantity || product?.listingQuantity || 1,
          });
        }
      }

      setProductData(enhancedData);
      setExpandedOrder({
        ...order,
        items: expandedItems.length > 0 ? expandedItems : order.items,
      });
    } catch (error) {
      console.error('Error loading product data:', error);
      // Don't show error to user as this is enhancement data, not critical
      setExpandedOrder(order);
    } finally {
      setIsLoadingProducts(false);
    }
  };

  const handleEditField = (field: keyof DhlCsvRowData, value: string) => {
    setEditableData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleNextStep = () => {
    if (currentStep === 'preview') {
      setCurrentStep('edit');
    } else if (currentStep === 'edit') {
      setCurrentStep('package');
    } else if (currentStep === 'package') {
      if (!selectedPackageId) {
        setError('Please select package dimensions before proceeding');
        return;
      }
      setCurrentStep('generate');
    }
  };

  const handlePreviousStep = () => {
    if (currentStep === 'generate') {
      setCurrentStep('package');
    } else if (currentStep === 'package') {
      setCurrentStep('edit');
    } else if (currentStep === 'edit') {
      setCurrentStep('preview');
    }
  };

  const handleGenerateCsv = async () => {
    if (!expandedOrder || !order || !selectedPackage || csvData.length === 0) {
      setError('Missing required data for CSV generation');
      return;
    }

    try {
      setIsGenerating(true);
      
      const csvContent = convertToCsvString(csvData);
      const filename = generateDhlCsvFilename(order);
      
      downloadCsvFile(csvContent, filename);
      
      toast.success(`CSV exported successfully: ${filename}`);
      onClose();
    } catch (error) {
      console.error('Error generating CSV:', error);
      setError('Failed to generate CSV file. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  if (!order) return null;

  const getStepTitle = () => {
    switch (currentStep) {
      case 'preview': return 'Order Preview';
      case 'edit': return 'Edit Export Data';
      case 'package': return 'Select Package Dimensions';
      case 'generate': return 'Generate CSV Export';
      default: return 'DHL Export';
    }
  };

  const getStepDescription = () => {
    switch (currentStep) {
      case 'preview': return 'Review the order details that will be exported';
      case 'edit': return 'Customize the export data as needed';
      case 'package': return 'Select package dimensions for accurate shipping calculations';
      case 'generate': return 'Generate and download your DHL CSV file';
      default: return '';
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-md z-[10200] flex items-center justify-center p-4"
          style={{
            margin: 0,
            padding: "1rem",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
          }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ 
              type: "spring",
              duration: 0.5,
              bounce: 0.3 
            }}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col overflow-hidden"
          >
            {/* Enhanced Header */}
            <div className="relative bg-gradient-to-r from-purple-600 to-indigo-600 p-6 flex-shrink-0">
              <div className="absolute inset-0 bg-gradient-to-r from-purple-600/20 to-transparent"></div>
              <div className="relative flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <motion.div 
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.2, type: "spring", bounce: 0.6 }}
                    className="h-12 w-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/30"
                  >
                    <FileDown className="h-6 w-6 text-white" />
                  </motion.div>
                  <div>
                    <motion.h2 
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.1 }}
                      className="text-2xl font-bold text-white"
                    >
                      DHL CSV Export
                    </motion.h2>
                    <motion.p 
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.2 }}
                      className="text-white/80 text-sm"
                    >
                      Order #{order.orderNumber || order.id.substring(0, 8)} • {formatCurrency(order.total)}
                    </motion.p>
                  </div>
                </div>
                <motion.div
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={onClose}
                    className="text-white hover:bg-white/20 h-10 w-10 rounded-full"
                  >
                    <X className="h-5 w-5" />
                  </Button>
                </motion.div>
              </div>
            </div>

            {/* Step Progress Indicator */}
            <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-gray-900">{getStepTitle()}</h3>
                  <p className="text-sm text-gray-600">{getStepDescription()}</p>
                </div>
                <div className="flex items-center gap-2">
                  {['preview', 'edit', 'package', 'generate'].map((step, index) => (
                    <div
                      key={step}
                      className={`w-3 h-3 rounded-full transition-colors ${
                        step === currentStep 
                          ? 'bg-purple-600' 
                          : ['preview', 'edit', 'package', 'generate'].indexOf(currentStep) > index
                          ? 'bg-green-500'
                          : 'bg-gray-300'
                      }`}
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto bg-gradient-to-b from-gray-50/50 to-white">
              <div className="p-6">
                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6"
                  >
                    <div className="flex items-center gap-2 text-red-800">
                      <AlertCircle className="h-5 w-5" />
                      <span className="font-medium">{error}</span>
                    </div>
                  </motion.div>
                )}

                {/* Step Content */}
                {currentStep === 'preview' && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="space-y-6"
                  >
                    {/* Order Overview */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {/* Order Details */}
                      <Card>
                        <CardHeader>
                          <CardTitle className="flex items-center gap-2">
                            <Package className="h-5 w-5 text-purple-600" />
                            Order Information
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          <div>
                            <Label className="text-sm font-medium text-gray-700">Order Number</Label>
                            <p className="text-gray-900 font-medium">{order.orderNumber || order.id}</p>
                          </div>
                          <div>
                            <Label className="text-sm font-medium text-gray-700">Date</Label>
                            <p className="text-gray-900">{formatDate(order.createdAt)}</p>
                          </div>
                          <div>
                            <Label className="text-sm font-medium text-gray-700">Total Items</Label>
                            <p className="text-gray-900">{order.items.length}</p>
                          </div>
                          <div>
                            <Label className="text-sm font-medium text-gray-700">Total Value</Label>
                            <p className="text-gray-900 font-semibold">{formatCurrency(order.total)}</p>
                          </div>
                        </CardContent>
                      </Card>

                      {/* Customer Details */}
                      <Card>
                        <CardHeader>
                          <CardTitle className="flex items-center gap-2">
                            <Globe className="h-5 w-5 text-purple-600" />
                            Destination
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          <div>
                            <Label className="text-sm font-medium text-gray-700">Name</Label>
                            <p className="text-gray-900 font-medium">{order.customer.name}</p>
                          </div>
                          <div>
                            <Label className="text-sm font-medium text-gray-700">Email</Label>
                            <p className="text-gray-900">{order.customer.email}</p>
                          </div>
                          <div>
                            <Label className="text-sm font-medium text-gray-700">Phone</Label>
                            <p className="text-gray-900">{order.customer.phone || 'Not provided'}</p>
                          </div>
                          <div>
                            <Label className="text-sm font-medium text-gray-700">Address</Label>
                            <div className="text-gray-900 text-sm leading-relaxed">
                              {order.customer.address?.line1}<br />
                              {order.customer.address?.line2 && (
                                <>{order.customer.address.line2}<br /></>
                              )}
                              {order.customer.address?.city}, {order.customer.address?.state} {order.customer.address?.postal_code}<br />
                              {order.customer.address?.country}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </div>

                    {/* Items List */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <Package className="h-5 w-5 text-purple-600" />
                          Items to Export ({order.items.length})
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          {order.items.map((item, index) => (
                            <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                              <div className="flex items-center gap-3">
                                <img
                                  src={item.image}
                                  alt={item.name}
                                  className="w-12 h-12 object-cover rounded-lg"
                                />
                                <div>
                                  <p className="font-medium text-gray-900">{item.name}</p>
                                  <p className="text-sm text-gray-600">Quantity: {item.quantity}</p>
                                </div>
                              </div>
                              <div className="text-right">
                                <p className="font-semibold text-gray-900">
                                  {formatCurrency(item.onSale && item.onSalePrice ? item.onSalePrice : item.price)}
                                </p>
                                {item.onSale && item.onSalePrice && (
                                  <p className="text-sm text-gray-500 line-through">
                                    {formatCurrency(item.price)}
                                  </p>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                )}

                {currentStep === 'edit' && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="space-y-6"
                  >
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <Edit2 className="h-5 w-5 text-purple-600" />
                          Customize Export Data
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="company">Company Name</Label>
                            <Input
                              id="company"
                              value={editableData.company || ''}
                              onChange={(e) => handleEditField('company', e.target.value)}
                              placeholder="Leave blank as per DHL requirements"
                            />
                          </div>
                          <div>
                            <Label htmlFor="shippingMethod">Shipping Method</Label>
                            <Input
                              id="shippingMethod"
                              value={editableData.shippingMethod || 'DHL Express Intl'}
                              onChange={(e) => handleEditField('shippingMethod', e.target.value)}
                            />
                          </div>
                          <div>
                            <Label htmlFor="contents">Contents Description</Label>
                            <Input
                              id="contents"
                              value={editableData.contents || ''}
                              onChange={(e) => handleEditField('contents', e.target.value)}
                              placeholder="Optional contents description"
                            />
                          </div>
                          <div>
                            <Label htmlFor="instructions">Special Instructions</Label>
                            <Input
                              id="instructions"
                              value={editableData.instructions || ''}
                              onChange={(e) => handleEditField('instructions', e.target.value)}
                              placeholder="Optional delivery instructions"
                            />
                          </div>
                          <div>
                            <Label htmlFor="signatureRequired">Signature Required</Label>
                            <Select
                              value={editableData.signatureRequired || 'No'}
                              onValueChange={(value) => handleEditField('signatureRequired', value)}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Yes">Yes</SelectItem>
                                <SelectItem value="No">No</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label htmlFor="atl">ATL (Authority to Leave)</Label>
                            <Select
                              value={editableData.atl || 'Yes'}
                              onValueChange={(value) => handleEditField('atl', value)}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Yes">Yes</SelectItem>
                                <SelectItem value="No">No</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                )}

                {currentStep === 'package' && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="space-y-6"
                  >
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <Box className="h-5 w-5 text-purple-600" />
                          Select Package Dimensions
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-6">
                        {isLoadingPackages ? (
                          <div className="flex items-center justify-center py-8">
                            <Loader2 className="h-6 w-6 animate-spin text-purple-600 mr-2" />
                            <span className="text-gray-600">Loading package dimensions...</span>
                          </div>
                        ) : packageDimensions.length === 0 ? (
                          <div className="text-center py-8">
                            <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                            <p className="text-gray-600">No package dimensions configured</p>
                            <p className="text-sm text-gray-500">Please configure package dimensions in Settings</p>
                          </div>
                        ) : (
                          <>
                            <div>
                              <Label htmlFor="packageSelect" className="text-base font-medium">
                                Package Dimensions *
                              </Label>
                              <Select 
                                value={selectedPackageId} 
                                onValueChange={setSelectedPackageId}
                              >
                                <SelectTrigger className="mt-2">
                                  <SelectValue placeholder="Select package dimensions" />
                                </SelectTrigger>
                                <SelectContent>
                                  {packageDimensions.map((pkg) => (
                                    <SelectItem key={pkg.id} value={pkg.id!}>
                                      <div className="flex flex-col">
                                        <span className="font-medium">{pkg.name}</span>
                                        <span className="text-sm text-gray-500">
                                          {pkg.length}" × {pkg.width}" × {pkg.height}" • {pkg.weight} oz
                                        </span>
                                      </div>
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>

                            {selectedPackage && (
                              <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                className="bg-purple-50 rounded-lg p-4"
                              >
                                <h4 className="font-medium text-purple-900 mb-3">Selected Package Details</h4>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                  <div>
                                    <span className="text-sm text-purple-700 font-medium">Length</span>
                                    <p className="text-sm text-purple-600">
                                      {selectedPackage.length}" ({inchesToCm(selectedPackage.length)} cm)
                                    </p>
                                  </div>
                                  <div>
                                    <span className="text-sm text-purple-700 font-medium">Width</span>
                                    <p className="text-sm text-purple-600">
                                      {selectedPackage.width}" ({inchesToCm(selectedPackage.width)} cm)
                                    </p>
                                  </div>
                                  <div>
                                    <span className="text-sm text-purple-700 font-medium">Height</span>
                                    <p className="text-sm text-purple-600">
                                      {selectedPackage.height}" ({inchesToCm(selectedPackage.height)} cm)
                                    </p>
                                  </div>
                                  <div>
                                    <span className="text-sm text-purple-700 font-medium">Weight</span>
                                    <p className="text-sm text-purple-600">
                                      {selectedPackage.weight} oz ({ouncesToKg(selectedPackage.weight)} kg)
                                    </p>
                                  </div>
                                </div>
                                {selectedPackage.description && (
                                  <p className="text-sm text-purple-600 mt-2">{selectedPackage.description}</p>
                                )}
                              </motion.div>
                            )}
                          </>
                        )}
                      </CardContent>
                    </Card>
                  </motion.div>
                )}

                {currentStep === 'generate' && csvData.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="space-y-6"
                  >
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <Eye className="h-5 w-5 text-purple-600" />
                          CSV Preview
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="bg-gray-50 rounded-lg p-4 mb-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium text-gray-900">Export Summary</p>
                              <p className="text-sm text-gray-600">
                                {csvData.length} row{csvData.length !== 1 ? 's' : ''} will be exported
                              </p>
                            </div>
                            <Badge className="bg-green-100 text-green-800">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Ready to Export
                            </Badge>
                          </div>
                        </div>

                        <div className="overflow-x-auto max-h-96">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Item Name</TableHead>
                                <TableHead>Qty</TableHead>
                                <TableHead>Price</TableHead>
                                <TableHead>Weight (kg)</TableHead>
                                <TableHead>Dimensions (cm)</TableHead>
                                <TableHead>Destination</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {csvData.map((row, index) => (
                                <TableRow key={index}>
                                  <TableCell className="font-medium">{row.itemName}</TableCell>
                                  <TableCell>{row.qty}</TableCell>
                                  <TableCell>{row.itemPrice} {row.declaredValueCurrency}</TableCell>
                                  <TableCell>{row.weight}</TableCell>
                                  <TableCell>
                                    {row.packageLength} × {row.packageWidth} × {row.packageHeight}
                                  </TableCell>
                                  <TableCell>
                                    {row.destinationCity}, {row.destinationCountry}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="bg-gray-50 border-t border-gray-200 p-6 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-500">
                  {currentStep === 'preview' && 'Step 1 of 4: Review Order'}
                  {currentStep === 'edit' && 'Step 2 of 4: Customize Data'}
                  {currentStep === 'package' && 'Step 3 of 4: Select Package'}
                  {currentStep === 'generate' && 'Step 4 of 4: Generate Export'}
                </div>
                
                <div className="flex items-center gap-4">
                  {currentStep !== 'preview' && (
                    <Button 
                      variant="outline" 
                      onClick={handlePreviousStep}
                      disabled={isGenerating || isLoadingPackages || isLoadingProducts}
                    >
                      Previous
                    </Button>
                  )}
                  
                  <Button 
                    variant="outline" 
                    onClick={onClose}
                    disabled={isGenerating || isLoadingPackages || isLoadingProducts}
                  >
                    Cancel
                  </Button>

                  {currentStep !== 'generate' ? (
                    <Button 
                      onClick={handleNextStep}
                      disabled={isLoadingPackages || isLoadingProducts}
                      className="bg-purple-600 hover:bg-purple-700 text-white"
                    >
                      {isLoadingPackages || isLoadingProducts ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Loading...
                        </>
                      ) : (
                        'Next'
                      )}
                    </Button>
                  ) : (
                    <Button 
                      onClick={handleGenerateCsv}
                      disabled={isGenerating || csvData.length === 0}
                      className="bg-purple-600 hover:bg-purple-700 text-white"
                    >
                      {isGenerating ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Generating...
                        </>
                      ) : (
                        <>
                          <Download className="w-4 h-4 mr-2" />
                          Download CSV
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
